import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import strings from "../Language"; 

type Variant = "danger" | "warning" | "info" | "success" | "neutral";

type ExtraAction = {
  label: React.ReactNode;
  value: string;             // value returned when this button is pressed
  variant?: Variant;
  autoFocus?: boolean;
};

type ConfirmOptions = {
  title: React.ReactNode;
  message: React.ReactNode;
  /** Dialog tone and primary button color */
  variant?: Variant;
  /** Custom confirm and cancel button labels */
  confirmText?: React.ReactNode;
  cancelText?: React.ReactNode;
  /** Extra actions (e.g., "Only this one", "This and next ones", etc.) */
  extraActions?: ExtraAction[];
  /** Default focus target when modal opens */
  defaultFocus?: "confirm" | "cancel" | "firstExtra";
  /** Allow ESC to close the modal (resolves to "cancel") */
  dismissible?: boolean;
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<"confirm" | "cancel" | string>;

const ConfirmContext = createContext<{ confirm: ConfirmFn } | null>(null);

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx.confirm;
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<(value: "confirm" | "cancel" | string) => void>();

  const handleResolve = useCallback((value: "confirm" | "cancel" | string) => {
    setOpen(false);
    // Defer resolution to allow animations/closing
    setTimeout(() => {
      resolverRef.current?.(value);
    }, 0);
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts({
      variant: "neutral",
      confirmText: strings.ConfirmProvider.common.accept,
      cancelText: strings.ConfirmProvider.common.cancel,
      dismissible: true,
      defaultFocus: "cancel",
      ...options,
    });
    setOpen(true);
    return new Promise<"confirm" | "cancel" | string>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  // Map variants to Bootstrap button classes
  const btnClassFor = (v: Variant | undefined, isOutline = false) => {
    const suffix =
      v === "neutral" || !v ? "secondary" :
      v === "danger" ? "danger" :
      v === "warning" ? "warning" :
      v === "success" ? "success" : "info";
    return `btn ${isOutline ? `btn-outline-${suffix}` : `btn-${suffix}`}`;
  };

  // Manage accessible focus
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const firstExtraRef = useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!open) return;
    // Default focus: Cancel (less destructive action)
    const focusTarget =
      (opts?.defaultFocus === "confirm" && confirmBtnRef.current) ||
      (opts?.defaultFocus === "firstExtra" && firstExtraRef.current) ||
      cancelBtnRef.current;
    focusTarget?.focus();
  }, [open, opts?.defaultFocus]);

  // Handle ESC and Enter keys
  React.useEffect(() => {
    if (!open || !opts?.dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleResolve("cancel");
      }
      if (e.key === "Enter" && document.activeElement === confirmBtnRef.current) {
        // Allow Enter only when confirm button is focused
        e.preventDefault();
        handleResolve("confirm");
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, opts?.dismissible, handleResolve]);

  const modal = open && opts ? (
    <div
      aria-modal="true"
      role="dialog"
      aria-labelledby="confirm-title"
      aria-describedby="confirm-message"
      className="modal d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onMouseDown={(e) => {
        const target = e.target as HTMLElement;
        // Click on backdrop closes modal if dismissible
        if (target.classList.contains("modal") && opts.dismissible) {
          handleResolve("cancel");
        }
      }}
    >
      <div className="modal-dialog modal-dialog-centered" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 id="confirm-title" className="modal-title">
              {opts.title}
            </h5>
            {opts.dismissible && (
              <button
                type="button"
                className="close"
                aria-label={strings.ConfirmProvider.common.close}
                onClick={() => handleResolve("cancel")}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            )}
          </div>

          <div className="modal-body">
            <div id="confirm-message">{opts.message}</div>
          </div>

          <div className="modal-footer d-flex gap-2">
            {/* Extra action buttons */}
            {opts.extraActions?.map((a, idx) => (
              <button
                key={a.value}
                type="button"
                ref={idx === 0 ? firstExtraRef : undefined}
                className={btnClassFor(a.variant, true)}
                onClick={() => handleResolve(a.value)}
              >
                {a.label}
              </button>
            ))}

            <div className="mr-auto" />

            {/* Cancel button */}
            <button
              type="button"
              ref={cancelBtnRef}
              className="btn btn-outline-secondary"
              onClick={() => handleResolve("cancel")}
            >
              {opts.cancelText ?? strings.ConfirmProvider.common.cancel}
            </button>

            {/* Confirm button */}
            <button
              type="button"
              data-testid="confirm-provider-button"
              ref={confirmBtnRef}
              className={btnClassFor(opts.variant)}
              onClick={() => handleResolve("confirm")}
            >
              {opts.confirmText ?? strings.ConfirmProvider.common.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {createPortal(modal, document.body)}
    </ConfirmContext.Provider>
  );
};
